import { CopyrightLine } from './CopyrightLine.js';
import { VendorCredit } from './VendorCredit.js';

/* ===========================================================================
 * THE ONE FOOTER — who built this.
 *
 * It exists because the only place the vendor was named was the sign-in
 * screen, which is the one screen a person having a problem has already left.
 *
 * NO SUPPORT ROUTE IN THE FOOTER (owner, 2026-09-22). It carried a WhatsApp
 * link, then a mail address, and now neither. The reasoning that removed the
 * number applies to the address as well once the decision is "the vendor is
 * NAMED here, and support is reached the way this tenant already reaches it":
 * a channel printed on every screen of the product is one the reader treats as
 * the official door, and it was not. What is left is attribution — who built
 * this — which is the thing the line was for.
 *
 * The test beside this file pins the ABSENCE of all three (mail, WhatsApp,
 * telephone) rather than being deleted for no longer matching, so a support
 * route cannot drift back in unnoticed.
 *
 * WHERE IT SITS, AND WHY IT IS NOT INSIDE THE READING COLUMN. It is the LAST
 * CHILD OF `main`, a sibling of the padded wrapper that holds the outlet, and
 * it carries its own inset. It used to live inside that wrapper, which is the
 * reason it kept missing the floor: `mt-auto` pushes an item to the end of its
 * parent's CONTENT box, and the wrapper's bottom padding — a full page gutter —
 * was drawn below that, so the footer stopped a gutter short of the bottom no
 * matter how the height chain was arranged. (Two earlier passes chased the
 * height chain instead; the second cut the padding from 96px to 34 and left the
 * gap.) Out here the wrapper's bottom padding is the air ABOVE the footer,
 * where it belongs as separation from the content, and the block below the
 * footer is the footer's own.
 *
 * `mt-auto` stays, and `main` is a flex column, so the rule holds in both
 * directions: on a short screen the footer sits on the floor of the viewport
 * rather than floating under three inches of nothing, and on a long one it is
 * the last thing after the content, reached by scrolling.
 *
 * ALIGNED WITH THE RAIL'S BUILD STAMP (owner). Both are the same kind of line —
 * the quiet, permanent one at the bottom of a column — so both close with 8px:
 * `py-element` here against `AppVersion`'s `py-2`, on the same 12.5/12px step
 * and the same tight leading. The two now land on one floor line instead of the
 * footer sitting a gutter higher than the stamp beside it.
 *
 * THE PHONE'S BOTTOM NAVIGATION IS NOT THIS COMPONENT'S PROBLEM ANY MORE. It
 * carried `pb-24` to clear `MobileBottomNav`, which cleared the END of a scroll
 * and did nothing for the middle of one — a list still ran behind the capsule
 * on its way past. `AppShell` reserves the whole band outside the scrolling
 * column now (see its note), so the footer is just a footer again and spaces
 * itself the same at every width.
 *
 * THE OVERVIEW IS THE EXCEPTION (owner, 2026-10-29: "this is our home page
 * for admin"): its board keeps its fixed `--h-board` and this line follows
 * it, reached by scrolling (footerRoutes `showsFooter`).
 *
 * `AppShell` keeps it off the other WIDE routes — the full-bleed boards, whose
 * container is `h-full` and whose child owns the whole viewport; appending a
 * block to that column makes the board shorter than its own scrollport and
 * gives a map a scrollbar. Somebody on the map who needs support is one
 * navigation away from a screen with it.
 *
 * AND IT DOES NOT RENDER ON A PHONE. `AppShell` decides that — see its note.
 * The line itself moved to `VendorCredit`, because the phone's profile page shows it
 * instead and a credit written out twice is a credit that drifts.
 * ======================================================================== */

export function AppFooter(): JSX.Element {
  return (
    /* THE SAME QUIET FLOW AS THE VERSION STAMP (owner). It was `text-copy-sm`
       over `pt-component` — a 13px line with a full component's worth of air
       above it, which made the page's least important sentence its tallest
       band. The rail's build stamp is the right reference: it says something
       operationally real in 12.5px on tight leading and takes almost no room.
       Same step, same leading, an element's gap instead of a component's.

       TWO BOXES, because the footer now carries its own inset. The outer one
       REPEATS THE SHELL WRAPPER'S BOX EXACTLY — `mx-auto max-w-content` plus
       the same `--gutter` pair — so the words and the hairline keep the width
       and the alignment they had inside it; only the block padding is this
       component's own, and only below the line. The inner one is the row. */
    <footer className="mt-auto mx-auto w-full max-w-content px-gutter-sm pb-element md:px-gutter">
      {/* Copyright at the start, the vendor at the end (owner, 2026-10-26). */}
      <div className="flex flex-wrap items-center justify-between gap-x-component gap-y-tight border-t border-border pt-element text-label font-normal leading-tight text-muted-foreground">
        <CopyrightLine />
        <VendorCredit />
      </div>
    </footer>
  );
}
